<script setup lang="ts">
import {
  calculateAverage,
  getCurrentLeg,
  getLegs,
  getLegThrowsForParticipant,
  getMostThrowsInLeg,
  getWinningThrowForLeg,
} from '~/services/DartsThrowHelper'
import type { DartsThrow } from '~/stores/localGameStore'

const props = defineProps<{
  participants: [{ id: string | number, name: string }, { id: string | number, name: string }]
  dartsThrows: Array<DartsThrow>
  startingScore: number
}>()

const currentLeg = computed(() => getCurrentLeg(props.dartsThrows))
</script>

<template>
  <div
    v-for="leg in getLegs(dartsThrows)"
    :key="leg"
  >
    <div class="flex flex-col gap-4 mb-4 relative">
      <div class="text-center font-bold text-3xl sticky -top-4 left-0 right-0 py-2 bg-white/50 backdrop-blur">
        Leg {{ leg }}
        <template v-if="currentLeg === leg && !getWinningThrowForLeg(dartsThrows, leg)">
          <span class="text-2xl">
            {{ $t('Ongoing') }}
          </span>
        </template>
      </div>
      <div class="grid grid-cols-3 gap-2 text-xl lg:text-2xl border-b pb-2 mb-2">
        <div
          class="text-right flex-wrap flex flex-row gap-1 justify-end items-end"
          :class="{ 'font-bold': getWinningThrowForLeg(dartsThrows, leg)?.pid === participants[0].id }"
        >
          <div>{{ participants[0].name }}</div>
          <div class="text-base">
            (Avg: {{ calculateAverage(getLegThrowsForParticipant(participants[0].id, dartsThrows, leg, startingScore), participants[0].id) }})
          </div>
        </div>
        <div>
          &nbsp;
        </div>
        <div
          class="flex-wrap flex flex-row gap-1 items-end"
          :class="{ 'font-bold': getWinningThrowForLeg(dartsThrows, leg)?.tpid === participants[1].id }"
        >
          <div>{{ participants[1].name }}</div>
          <div class="text-base">
            (Avg: {{ calculateAverage(getLegThrowsForParticipant(participants[1].id, dartsThrows, leg, startingScore), participants[1].id) }})
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2 text-lg xl:text-xl">
        <div class="flex flex-col text-right">
          <div
            v-for="dartsThrow in getLegThrowsForParticipant(participants[0].id, dartsThrows, leg, startingScore)"
            :key="`legthrow ${dartsThrow.ulid}`"
            :class="{ 'font-bold': dartsThrow.ft }"
          >
            <template v-if="dartsThrow.ft">
              🎯
            </template>
            {{ dartsThrow.s }}
            <template v-if="dartsThrow.ft">
              ({{ dartsThrow.du }}D)
            </template>
            <template v-else>
              ({{ dartsThrow.remaining }})
            </template>
          </div>
        </div>
        <div class="flex flex-col text-center">
          <div
            v-for="number in getMostThrowsInLeg(leg, dartsThrows, participants[0].id, participants[1].id)"
            :key="`legthrow ${number}`"
          >
            {{ number }}
            <template
              v-if="number === getMostThrowsInLeg(leg, dartsThrows, participants[0].id, participants[1].id).length"
            >
              ({{ (number * 3) - (3 - (getWinningThrowForLeg(dartsThrows, leg)?.du ?? 3)) }}D)
            </template>
            <template v-else>
              ({{ number * 3 }}D)
            </template>
          </div>
        </div>
        <div class="flex flex-col">
          <div
            v-for="dartsThrow in getLegThrowsForParticipant(participants[1].id, dartsThrows, leg, startingScore)"
            :key="`legthrow ${dartsThrow.ulid}`"
            :class="{ 'font-bold': dartsThrow.ft }"
          >
            <template v-if="dartsThrow.ft">
              🎯
            </template>
            <span :class="{ 'font-bold': dartsThrow.s === 180 }">
              {{ dartsThrow.s }}
            </span>
            <template v-if="dartsThrow.ft">
              ({{ dartsThrow.du }}D)
            </template>
            <template v-else>
              ({{ dartsThrow.remaining }})
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
